/**
 * CapabilityJobStatus - Shows job status for a specific capability
 *
 * Located in attacks/capabilities/ because this component is only used
 * by CapabilityDrawer (proper ownership).
 *
 * Uses shared ScanStatus components for unified UI across Asset,
 * Vulnerability, and Capability drawers.
 */
import { useMemo, useState } from 'react';

import {
  getScanStatusConfig,
  ScanStatusBox,
  ScanStatusModal,
} from '@/components/ScanStatus';
import { useCancelJob, useReRunJob } from '@/hooks/useJobs';
import { useCapabilityJobs } from '@/sections/attacks/capabilities/hooks';
import { downloadFile } from '@/sections/jobs/utils/downloadHelpers.util';
import { cn } from '@/utils/classname';

import { CapabilityJobStatusProps } from './types';

/**
 * Variant-to-dot-color map for the compact chip. Mirrors the semantic
 * intent of the StatusChip variants without bringing the legacy chip's
 * pill background into the new dense header layout.
 */
const DOT_COLORS: Record<string, string> = {
  muted: 'bg-chariot-text-tertiary',
  default: 'bg-chariot-text-secondary',
  info: 'bg-chariot-brand-primary',
  success: 'bg-chariot-status-success',
  warning: 'bg-chariot-status-warning',
  critical: 'bg-chariot-status-critical',
  pending: 'bg-chariot-status-warning animate-pulse',
};

export function CapabilityJobStatus({
  capability,
  compact = false,
}: CapabilityJobStatusProps) {
  const [open, setOpen] = useState(false);
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  const { jobs, status, isLoading, error, refetch } =
    useCapabilityJobs(capability);

  const statusConfig = useMemo(() => getScanStatusConfig(status), [status]);

  const actionHandlers = useMemo(
    () => ({
      handleRerun: (jobKey: string) => reRunJob({ jobKey }),
      handleCancel: (jobKey: string) => cancelJob({ jobKey }),
      handleDownload: (url: string, filename: string) =>
        downloadFile(url, filename),
    }),
    [reRunJob, cancelJob]
  );

  if (!capability) {
    return null;
  }

  const disabled = !status;
  const chipLabel = statusConfig?.label ?? 'No scans';
  const dotColor =
    DOT_COLORS[(statusConfig?.variant as string) ?? 'muted'] ??
    DOT_COLORS.muted;

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          aria-label="View scan status details"
          className={cn(
            'border-chariot-stroke bg-chariot-background-secondary/60 text-chariot-text-secondary focus-visible:ring-chariot-brand-primary inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium tracking-wide whitespace-nowrap focus:outline-hidden focus-visible:ring-2',
            !disabled && 'hover:bg-chariot-background-tertiary cursor-pointer',
            disabled && 'cursor-not-allowed opacity-80'
          )}
        >
          <span
            aria-hidden="true"
            className={cn('size-1.5 shrink-0 rounded-full', dotColor)}
          />
          <span>{chipLabel}</span>
        </button>
      ) : (
        <ScanStatusBox
          statusConfig={statusConfig}
          isLoading={isLoading}
          onViewDetails={() => setOpen(true)}
          disabled={disabled}
        />
      )}
      <ScanStatusModal
        open={open}
        onClose={() => setOpen(false)}
        title={`${capability.name} Scans`}
        jobs={jobs}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        actionHandlers={actionHandlers}
      />
    </>
  );
}
